// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.
import React, { useState, useMemo, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection, getDisplayLoanBooks } from '../../utils/loanBookResolution';
import { useUserData } from '../../hooks/useUserData';
import { useDischargedLoans } from '../../hooks/useDischargedLoans';
import ReengagementActionCell from '../../components/reengagement/ReengagementActionCell';
import { useReengagementEligibility } from '../../hooks/useReengagementEligibility';
import type { ReengagementEligibility } from '../../types/dischargedReengagement';
import { useCompanyDataYearRange } from '../../hooks/useCompanyDataYearRange';
import { useCompanyData } from '../../hooks/useCompanyData';
import { useLostLoans, LossReason, LostLoansMetrics, ALL_LOSS_REASONS, STANDARD_LOSS_REASONS, LOSS_REASON_LABELS, isRetainedLossReason } from '../../hooks/useLostLoans';
import { useRefinanceData, OutcomeType, RelationshipType } from '../../hooks/useRefinanceData';
import ManualLinkControl from '../../components/refinance/ManualLinkControl';
import { useLoanContacts } from '../../hooks/useLoanContacts';
import {
  useTrailRiskStatusForRefs,
  periodMonthFromDate,
  refKey,
  type TrailRiskRef,
} from '../../hooks/useTrailRiskStatusForRefs';
import { TrailRiskFollowUp } from '../../components/contacts/TrailRiskFollowUp';
import { formatCurrency, formatCurrencyShorthand } from '../../utils/formatters';
import toast from 'react-hot-toast';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {
  FileX2,
  Banknote,
  Scale,
  TrendingUp,
  TrendingDown,
  ListChecks,
  BarChart3,
  Table as TableIcon,
  GitCompareArrows,
  Users,
  ChevronDown,
  ChevronRight,
  SearchCheck,
  Search,
  RefreshCw,
  Save,
  Check,
  Clock,
  X as XIcon,
  AlertTriangle,
  ArrowRight,
  Moon,
  Settings as SettingsIcon,
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { SkeletonReportPage } from '../../components/common/SkeletonLoader';
import StatCard from '../../components/common/StatCard';
import LossReasonStatCards from '../../components/refinance/LossReasonStatCards';
import { useAuth } from '../../hooks/useAuth';
import { BROKER_FILTERED_ADMIN_COPY } from '../../utils/brokerFilteredAdmin';
import BulkActionBar from '../../components/refinance/BulkActionBar';
import ContactTimeline from '../../components/contacts/ContactTimeline';
import DormantLoansSection from '../../components/dormancy/DormantLoansSection';
import { useDormantLoans, isDormancyEnabled } from '../../hooks/useDormantLoans';

/**
 * DischargedLoansPage — "Discharges"
 *
 * Track discharged loans, categorise why clients left (truly-discharged only),
 * record refinance outcomes (refinanced rows), and log client outreach. Mirrors
 * docs/mockups/discharges-page-redesign.html.
 *
 * Reuses the production "Lost Loans" engine (BigQuery lost_loan_reasons via
 * setLossReason), the shared refinance_matches store (verifyRefinanceMatch), and
 * the canonical loan-keyed contact store (useLoanContacts + ContactTimeline) so a
 * client's reason/outcome/contact history is single-source-of-truth and follows
 * them across every report.
 */

// Refinance outcome taxonomy (refinance_matches.outcome_type) — editable here AND
// on Refinance Intelligence (shared SSOT). Keys match useRefinanceData.OutcomeType.
const OUTCOME_LABELS: Record<OutcomeType, string> = {
  win_back: 'Win-Back (Same Broker)',
  internal_transfer: 'Internal Transfer (Different Broker)',
  cross_template_conversion: 'Cross-Loan Book Conversion',
  office_transfer: 'Office Transfer (Same Broker)',
};
const OUTCOME_KEYS: OutcomeType[] = [
  'win_back',
  'internal_transfer',
  'cross_template_conversion',
  'office_transfer',
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Illustrative trail-impact estimate (matches mockup): ~0.165%/yr of loan balance.
const trailImpactCents = (cents: number): number => Math.round(cents * 0.00165);

const RECORDS_PER_PAGE = 50;

/**
 * Which components of the attempt gate this loan has NOT met yet, in the same
 * order computeAttemptGate checks them (attempts, window span, distinct days).
 *
 * All three are enforced server-side, so naming only the attempt count made a
 * fully-worked loan read "5/1 attempts" while staying locked: the real blocker
 * was the window span, which is 0 for attempts logged on a single day.
 */
const describeGateShortfall = (e: ReengagementEligibility | undefined): string => {
  if (!e) return 'contact history not loaded yet';
  const missing: string[] = [];
  if (e.attemptCount < e.minAttempts) {
    missing.push(`${e.attemptCount}/${e.minAttempts} attempts`);
  }
  if (e.windowSpanDays < e.minWindowDays) {
    missing.push(`${e.windowSpanDays}/${e.minWindowDays} days between first and last attempt`);
  }
  if (e.requireDistinctDays && e.distinctDayCount < e.minAttempts) {
    missing.push(`${e.distinctDayCount}/${e.minAttempts} distinct days`);
  }
  return missing.length > 0 ? missing.join(', ') : 'attempt bar not met';
};

/**
 * The gated 'Uncontactable' <option> for the per-loan loss-reason editor.
 *
 * 'Uncontactable' is NOT a free reason: it is only offered when the tenant's
 * re-engagement feature is on, and only SELECTABLE once the broker has met the
 * re-engagement attempt bar for THIS loan (contactGatePassed). Until then it
 * renders disabled with a progress hint. The server (setLossReason) re-enforces
 * the same gate plus truly-discharged membership, so this is UX, not security.
 *
 * Renders nothing for tenants without the feature, EXCEPT when the loan is already
 * categorised 'uncontactable' (so an existing value still shows in the dropdown).
 */
const UncontactableLossOption: React.FC<{
  eligibility: ReengagementEligibility | undefined;
  currentReason: LossReason;
  reengagementEnabled: boolean;
}> = ({ eligibility, currentReason, reengagementEnabled }) => {
  const alreadySet = currentReason === 'uncontactable';
  if (!reengagementEnabled && !alreadySet) return null;
  const unlocked = eligibility?.contactGatePassed === true || alreadySet;
  const label = unlocked
    ? LOSS_REASON_LABELS.uncontactable
    : `${LOSS_REASON_LABELS.uncontactable} (locked, needs ${describeGateShortfall(eligibility)})`;
  return (
    <option value="uncontactable" disabled={!unlocked}>
      {label}
    </option>
  );
};

const DischargedLoansPage: React.FC = () => {
  // Loss reason, its notes and the contact log are all default-denied to a
  // broker-restricted admin at the callable chokepoint; disable, don't hide.
  const { isBrokerFilteredAdmin } = useAuth();
  const { userData } = useUserData();
  const { offices, companyData, loading: companyLoading } = useCompanyData();
  const { userVisibleTemplates, userVisibleOffices, loanBooks = [] } = useOutletContext<{
    userVisibleTemplates: any[];
    userVisibleOffices: any[];
    loanBooks: ResolvedLoanBook[];
  }>();

  const companyId = userData?.companyId || '';
  // WS5: who may classify a discharge outcome (link a successor, confirm
  // refinance/returning). Admin-family + broker-family (the grunt-work roles);
  // marketing + founder excluded. Broker-family is scoped to own loans server-side.
  const canClassify = ['super_admin', 'administrator', 'office_admin', 'broker', 'broker_assistant', 'credit_analyst'].includes(userData?.role ?? '');

  // Dormancy eligibility is decoupled from loanBookMode (Entourage migration
  // Phase 0). The Dormant tab + its feed surface when the tenant is NOT unified
  // OR has featureFlags.dormancy_retirement in {observe, enforce}. A unified
  // tenant WITHOUT the flag (Timms, Decru, VFA) stays hidden — its
  // active_loans_snapshot.is_active=FALSE rows are rolling-3-run roster
  // artifacts, NOT real dormancy. Entourage keeps its Dormant tab post-flip via
  // the flag. Gate OFF until the company doc resolves (companyData is null on
  // first render): otherwise a unified tenant briefly shows the tab + fires the
  // feed before the mode/flag load. !companyLoading holds it closed until known.
  const dormancyEnabled =
    !companyLoading &&
    isDormancyEnabled(companyData?.loanBookMode, companyData?.featureFlags?.dormancy_retirement);

  // ----- filters -----
  const [yearFilter, setYearFilter] = useState<number | 'all'>('all');
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [selectedBroker, setSelectedBroker] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState<'all' | string>('all');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'truly_discharged' | 'refinanced' | 'refinanced_pending' | 'returning_client' | 'returning_client_pending' | 'needs_review'>('all');
  const [categorizationStatus, setCategorizationStatus] = useState<'all' | 'needs_categorization' | 'completed'>('all');

  // ----- view + interaction state -----
  const [viewMode, setViewMode] = useState<'charts' | 'table'>('charts');
  const [activeTab, setActiveTab] = useState<'discharges' | 'dormant'>('discharges');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [selectedLoanIds, setSelectedLoanIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [brokerSectionCollapsed, setBrokerSectionCollapsed] = useState(false);
  const [reasonFilter, setReasonFilter] = useState<LossReason | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // ----- sorting -----
  const [detailSortColumn, setDetailSortColumn] = useState<'customer' | 'broker' | 'dischargeDate' | 'loanAmount' | 'status'>('dischargeDate');
  const [detailSortDirection, setDetailSortDirection] = useState<'asc' | 'desc'>('desc');
  const [brokerSortColumn, setBrokerSortColumn] = useState<'broker' | 'count' | 'totalValue' | 'refinancedCount' | 'returningCount' | 'retention' | 'trail'>('count');
  const [brokerSortDirection, setBrokerSortDirection] = useState<'asc' | 'desc'>('desc');

  const resolvedParams = useMemo(
    () => resolveLoanBookSelection(selectedTemplate, loanBooks),
    [selectedTemplate, loanBooks]
  );

  // ----- shared hooks: reason / outcome / contacts -----
  const { setLossReason, isSaving: isSavingReason, lastSaveError } = useLostLoans();
  const { verifyMatch, isVerifying, createManualRefinanceMatch } = useRefinanceData();
  const { contactsByLoan, mutateContact } = useLoanContacts(companyId);

  // Per-row local editor state for the reason editor (truly-discharged rows).
  const [reasonDraft, setReasonDraft] = useState<{ loanId: string; reason: LossReason; notes: string } | null>(null);
  // Mirror of the setLossReason `notes` cap (services/client-functions/src/refinance/setLossReasonSchema.ts).
  // Keep in sync with that Zod schema; the maxLength + counter stop a save from failing server-side.
  const LOSS_NOTES_MAX_LENGTH = 2000;
  // Per-row local editor state for the outcome editor (refinanced rows).
  const [outcomeDraft, setOutcomeDraft] = useState<{ loanId: string; outcomeType: OutcomeType | ''; notes: string } | null>(null);

  // ----- "Made by" broker list for ContactTimeline (mirrors AcquiredBooksPage) -----
  const [contactBrokerList, setContactBrokerList] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (!userData?.companyId) return;

    if (userData?.role === 'broker' || userData?.role === 'broker_assistant') {
      const brokerName = userData.commissionDataName || userData.displayName || '';
      if (brokerName) {
        setContactBrokerList([{ id: 'self', name: brokerName }]);
      }
      return;
    }

    const brokersQuery = query(collection(db, `companies/${userData.companyId}/brokers`));
    const unsubscribe = onSnapshot(
      brokersQuery,
      (snapshot) => {
        const brokers: { id: string; name: string }[] = [];
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.mergedInto) return;
          if ((data.status || 'active') !== 'active') return;
          if (data.currentName && typeof data.currentName === 'string') {
            brokers.push({ id: docSnap.id, name: data.currentName });
          }
        });
        brokers.sort((a, b) => a.name.localeCompare(b.name));
        setContactBrokerList(brokers);
      },
      (err) => {
        console.error('[DischargedLoansPage] Error fetching brokers for contact dropdown:', err);
      }
    );

    return () => unsubscribe();
  }, [userData?.companyId, userData?.role, userData?.commissionDataName, userData?.displayName]);

  const currentUser = useMemo(
    () => ({
      uid: userData?.uid,
      displayName: userData?.commissionDataName || userData?.displayName,
      email: userData?.email,
      role: userData?.role,
    }),
    [userData?.uid, userData?.commissionDataName, userData?.displayName, userData?.email, userData?.role]
  );

  // ----- year options (newest first) + All Years -----
  const { earliestYear, latestYear } = useCompanyDataYearRange({ companyId });
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const dynamicLatestYear = latestYear ?? currentYear;
    const dynamicEarliestYear = earliestYear ?? currentYear - 4;
    const years: number[] = [];
    for (let year = dynamicLatestYear; year >= dynamicEarliestYear; year--) years.push(year);
    return years;
  }, [earliestYear, latestYear]);

  // Reset template + broker selection when office changes (the broker set is
  // office-scoped, so the captured dropdown options below re-fill for the new office).
  useEffect(() => {
    setSelectedTemplate('all');
    setSelectedBroker('all');
  }, [selectedOffice]);

  // Reset pagination + expansion + selection when filters change.
  useEffect(() => {
    setCurrentPage(1);
    setExpandedRow(null);
    setSelectedLoanIds(new Set());
  }, [yearFilter, selectedOffice, selectedBroker, selectedTemplate, selectedStatus, categorizationStatus, reasonFilter, searchQuery]);

  // ----- data -----
  const {
    dischargedLoans,
    monthlyTrend,
    brokerBreakdown,
    lossReasonBreakdown,
    loading,
    error,
    totalDischargeCount,
    totalLoanValueCents,
    avgLoanSizeCents,
    totalLostTrailCents,
    refinancedCount,
    returningClientCount,
    refinancedPendingCount,
    returningClientPendingCount,
    trulyDischargedCount,
    categorisedCount,
    refinancedLoanValueCents,
    trulyDischargedLoanValueCents,
    trendGranularity,
    availableTemplateIds,
    refetch,
    patchReason,
    patchOutcome,
    restoreLoan,
  } = useDischargedLoans({
    companyId,
    year: yearFilter,
    yearType: 'calendar',
    officeId: selectedOffice === 'all' ? undefined : selectedOffice,
    brokerId: selectedBroker === 'all' ? undefined : selectedBroker,
    templateId: resolvedParams.templateId,
    entityCode: resolvedParams.entityCode,
  });

  // Dormant tab data (lifted here so the tab badge shows the needs-review count).
  const {
    dormantLoans,
    loading: dormantLoading,
    error: dormantError,
    totalLoanValueCents: dormantTotalValueCents,
    needsReviewCount: dormantNeedsReview,
    resolve: resolveDormant,
  } = useDormantLoans({
    companyId,
    enabled: dormancyEnabled,
    officeId: selectedOffice === 'all' ? undefined : selectedOffice,
    brokerId: selectedBroker === 'all' ? undefined : selectedBroker,
    templateId: resolvedParams.templateId,
    entityCode: resolvedParams.entityCode,
  });

  // Note: availableTemplateIds is surfaced by the hook for future loan-book-scoping
  // of the dropdown; the loan-book filter currently uses getDisplayLoanBooks(loanBooks).
  void availableTemplateIds;
  void userVisibleTemplates;

  // Brokers for the filter dropdown. The backend breakdown COLLAPSES to the single
  // selected broker once a broker filter is applied (the filter is server-side), which
  // would strand the user on one option ("just their name"). Capture the FULL list only
  // while the broker filter is 'all' so the dropdown always offers every broker. Office
  // change resets the broker to 'all' (above), so this re-fills per office too.
  const [availableBrokers, setAvailableBrokers] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (selectedBroker !== 'all') return;
    setAvailableBrokers(
      brokerBreakdown
        .map((b) => ({ id: b.brokerId, name: b.broker }))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [brokerBreakdown, selectedBroker]);

  // ----- derived metrics -----
  const refinanceRate = useMemo(() => {
    const denom = refinancedCount + trulyDischargedCount;
    return denom > 0 ? Math.round((refinancedCount / denom) * 100) : 0;
  }, [refinancedCount, trulyDischargedCount]);

  // Categorised % of truly-discharged loans (drives the "needs categorisation" workflow).
  // Uses the UNBOUNDED hook rollups (categorisedCount / trulyDischargedCount) — NOT the
  // ≤2000-row records slice, which would be wrong/self-contradictory on large tenants.
  const categorisedPct = useMemo(
    () => (trulyDischargedCount > 0 ? Math.round((categorisedCount / trulyDischargedCount) * 100) : 0),
    [categorisedCount, trulyDischargedCount]
  );

  const periodLabel = yearFilter === 'all' ? 'All years' : `${yearFilter} year to date`;

  // Fold lossReasonBreakdown (truly-discharged only) into the LostLoansMetrics shape.
  const lossMetrics: LostLoansMetrics = useMemo(() => {
    const byReason = {} as LostLoansMetrics['byReason'];
    const byReasonValueCents = {} as LostLoansMetrics['byReasonValueCents'];
    ALL_LOSS_REASONS.forEach((r) => {
      byReason[r] = 0;
      byReasonValueCents[r] = 0;
    });
    let total = 0;
    let totalValueCents = 0;
    lossReasonBreakdown.forEach((entry) => {
      const reason = (ALL_LOSS_REASONS.includes(entry.reason as LossReason)
        ? (entry.reason as LossReason)
        : 'unknown') as LossReason;
      byReason[reason] = (byReason[reason] || 0) + entry.count;
      byReasonValueCents[reason] = (byReasonValueCents[reason] || 0) + entry.valueCents;
      total += entry.count;
      totalValueCents += entry.valueCents;
    });
    return { total, totalValueCents, byReason, byReasonValueCents };
  }, [lossReasonBreakdown]);

  // ----- trend chart data (keyed on `period`, labelled by `trendGranularity`) -----
  const chartData = useMemo(() => {
    if (trendGranularity === 'year' || yearFilter === 'all') {
      // Per-year buckets. Sort ascending by year so bars read left→right oldest→newest.
      return [...monthlyTrend]
        .map((m) => ({
          label: m.period,
          count: m.count,
          amount: m.totalLoanValueCents,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    // Monthly: fill all 12 months so empty months still render a gap.
    const byPeriod = new Map(monthlyTrend.map((m) => [m.period, m]));
    return MONTH_NAMES.map((name, idx) => {
      const periodKey = `${yearFilter}-${String(idx + 1).padStart(2, '0')}`;
      const data = byPeriod.get(periodKey);
      return {
        label: name,
        count: data?.count || 0,
        amount: data?.totalLoanValueCents || 0,
      };
    });
  }, [monthlyTrend, trendGranularity, yearFilter]);

  const trendTitle =
    yearFilter === 'all' || trendGranularity === 'year'
      ? 'Discharge Trend — All Years'
      : `Monthly Discharge Trend — ${yearFilter}`;

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700">
          <p className="font-bold text-sm mb-2">{data.label}</p>
          <p className="text-blue-400 font-semibold">Count: {data.count}</p>
          <p className="text-green-400 font-semibold">Amount: {formatCurrency(data.amount)}</p>
        </div>
      );
    }
    return null;
  };

  // ----- refinance split donut -----
  // Counts AND dollar values come from the UNBOUNDED hook rollups so both halves of
  // each segment describe the same population (not the ≤2000-row records slice).
  const refinanceSplit = useMemo(
    () => [
      { name: 'Refinanced', value: refinancedCount, valueCents: refinancedLoanValueCents, color: '#10b981' },
      { name: 'Truly Discharged', value: trulyDischargedCount, valueCents: trulyDischargedLoanValueCents, color: '#ef4444' },
    ],
    [refinancedCount, trulyDischargedCount, refinancedLoanValueCents, trulyDischargedLoanValueCents]
  );

  // ----- broker breakdown (sortable) -----
  const sortedBrokers = useMemo(() => {
    const enriched = brokerBreakdown.map((b) => {
      const retention = b.count > 0 ? Math.round((b.refinancedCount / b.count) * 100) : 0;
      // Trail estimate from total value (we don't have per-broker book balance here).
      const trail = trailImpactCents(b.totalLoanValueCents);
      return { ...b, retention, trail };
    });
    return enriched.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      switch (brokerSortColumn) {
        case 'broker':
          aVal = a.broker.toLowerCase();
          bVal = b.broker.toLowerCase();
          break;
        case 'count':
          aVal = a.count;
          bVal = b.count;
          break;
        case 'totalValue':
          aVal = a.totalLoanValueCents;
          bVal = b.totalLoanValueCents;
          break;
        case 'refinancedCount':
          aVal = a.refinancedCount;
          bVal = b.refinancedCount;
          break;
        case 'returningCount':
          aVal = a.returningCount;
          bVal = b.returningCount;
          break;
        case 'retention':
          aVal = a.retention;
          bVal = b.retention;
          break;
        case 'trail':
          aVal = a.trail;
          bVal = b.trail;
          break;
        default:
          aVal = a.count;
          bVal = b.count;
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return brokerSortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return brokerSortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [brokerBreakdown, brokerSortColumn, brokerSortDirection]);

  const handleBrokerSort = (column: typeof brokerSortColumn) => {
    if (brokerSortColumn === column) {
      setBrokerSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setBrokerSortColumn(column);
      setBrokerSortDirection('desc');
    }
  };

  // ----- table: filter → sort → paginate -----
  const filteredLoans = useMemo(() => {
    return dischargedLoans.filter((loan) => {
      const isTruly = loan.refinanceStatus === 'truly_discharged';
      // 'needs_review' is a synthetic outcome spanning both pending states.
      if (selectedStatus === 'needs_review') {
        if (loan.refinanceStatus !== 'refinanced_pending' && loan.refinanceStatus !== 'returning_client_pending') return false;
      } else if (selectedStatus !== 'all' && loan.refinanceStatus !== selectedStatus) {
        return false;
      }
      if (categorizationStatus === 'needs_categorization' && (!isTruly || !!loan.lossReason)) return false;
      if (categorizationStatus === 'completed' && (!isTruly || !loan.lossReason)) return false;
      if (reasonFilter !== 'all') {
        if (!isTruly) return false;
        const r = (loan.lossReason || 'unknown') as LossReason;
        if (r !== reasonFilter) return false;
      }
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        const haystack = `${loan.customerName} ${loan.broker} ${loan.lender ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [dischargedLoans, selectedStatus, categorizationStatus, reasonFilter, searchQuery]);

  const sortedLoans = useMemo(() => {
    const sorted = [...filteredLoans].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      switch (detailSortColumn) {
        case 'customer':
          aVal = a.customerName.toLowerCase();
          bVal = b.customerName.toLowerCase();
          break;
        case 'broker':
          aVal = a.broker.toLowerCase();
          bVal = b.broker.toLowerCase();
          break;
        case 'dischargeDate':
          aVal = a.dischargeDate instanceof Date ? a.dischargeDate.getTime() : new Date(a.dischargeDate).getTime();
          bVal = b.dischargeDate instanceof Date ? b.dischargeDate.getTime() : new Date(b.dischargeDate).getTime();
          break;
        case 'loanAmount':
          aVal = a.loanAmountCents;
          bVal = b.loanAmountCents;
          break;
        case 'status': {
          const order = { truly_discharged: 0, refinanced_pending: 1, returning_client_pending: 2, refinanced: 3, returning_client: 4 };
          aVal = order[a.refinanceStatus] ?? 0;
          bVal = order[b.refinanceStatus] ?? 0;
          break;
        }
        default:
          aVal = 0;
          bVal = 0;
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return detailSortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return detailSortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
    return sorted;
  }, [filteredLoans, detailSortColumn, detailSortDirection]);

  const { paginatedLoans, totalPages, startIdx, endIdx } = useMemo(() => {
    const total = Math.ceil(sortedLoans.length / RECORDS_PER_PAGE) || 1;
    const start = (currentPage - 1) * RECORDS_PER_PAGE;
    const end = Math.min(start + RECORDS_PER_PAGE, sortedLoans.length);
    return { paginatedLoans: sortedLoans.slice(start, end), totalPages: total, startIdx: start, endIdx: end };
  }, [sortedLoans, currentPage]);

  // Trail-risk follow-up status (server-only client_events, RBAC-scoped),
  // resolved PER OCCURRENCE. A discharge event is keyed (loan, discharge month),
  // so the ref carries that month; the old per-loan lookup could hand a different
  // period's eventId to logClientContact and close the wrong incident.
  const trailRiskRefs = useMemo<TrailRiskRef[]>(
    () =>
      paginatedLoans
        .map((l: any) => {
          const periodMonth = periodMonthFromDate(l.dischargeDate);
          return periodMonth && l.loanId
            ? { loanId: l.loanId, eventType: 'loan_discharged' as const, periodMonth }
            : null;
        })
        .filter(Boolean) as TrailRiskRef[],
    [paginatedLoans],
  );
  const {
    statusByRef: trailRiskByRef,
    error: trailRiskError,
    refetch: refetchTrailRisk,
  } = useTrailRiskStatusForRefs(companyId, trailRiskRefs);

  // Discharged re-engagement: gated call-list + send. The server returns the
  // tenant `enabled` flag + thresholds + per-loan eligibility for the visible
  // page of loans (zero footprint when the feature is off for this company).
  const reengagementLoanIds = useMemo(
    () => paginatedLoans.map((l: any) => l.loanId).filter(Boolean),
    [paginatedLoans],
  );
  const {
    byLoan: reengagementByLoan,
    enabled: reengagementEnabled,
    loading: reengagementLoading,
    refetch: refetchReengagement,
  } = useReengagementEligibility(companyId, reengagementLoanIds);

  const handleDetailSort = (column: typeof detailSortColumn) => {
    if (detailSortColumn === column) {
      setDetailSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setDetailSortColumn(column);
      setDetailSortDirection('desc');
    }
    setCurrentPage(1);
  };

  const SortIndicator = ({ column }: { column: string }) => {
    if (column !== detailSortColumn) return <span className="ml-1 text-gray-600">↕</span>;
    return <span className="ml-1">{detailSortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  // ----- row expand / select -----
  const toggleExpand = (loanId: string) => {
    setExpandedRow((prev) => (prev === loanId ? null : loanId));
    // Prime the per-row editor drafts from the current record.
    const loan = dischargedLoans.find((l) => l.loanId === loanId);
    if (loan) {
      if (loan.refinanceStatus === 'truly_discharged') {
        setReasonDraft({
          loanId,
          reason: (loan.lossReason || 'unknown') as LossReason,
          notes: loan.lossNotes || '',
        });
        setOutcomeDraft(null);
      } else if (loan.refinanceMatch) {
        setOutcomeDraft({
          loanId,
          outcomeType: (loan.refinanceMatch.outcomeType || '') as OutcomeType | '',
          notes: loan.refinanceMatch.verificationNotes || '',
        });
        setReasonDraft(null);
      }
    }
  };

  const toggleSelect = (loanId: string) => {
    setSelectedLoanIds((prev) => {
      const next = new Set(prev);
      if (next.has(loanId)) next.delete(loanId);
      else next.add(loanId);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedLoanIds((prev) => {
      const next = new Set(prev);
      const allSelected = paginatedLoans.length > 0 && paginatedLoans.every((l) => next.has(l.loanId));
      paginatedLoans.forEach((l) => (allSelected ? next.delete(l.loanId) : next.add(l.loanId)));
      return next;
    });
  };

  const allOnPageSelected =
    paginatedLoans.length > 0 && paginatedLoans.every((l) => selectedLoanIds.has(l.loanId));

  // ----- save handlers -----
  const handleSaveReason = async (loanId: string) => {
    if (!reasonDraft || reasonDraft.loanId !== loanId) return;
    const reason = reasonDraft.reason;
    const notes = reasonDraft.notes || null;
    const ok = await setLossReason({
      companyId,
      // NEVER send an empty-string templateId: setLossReason's Zod schema is
      // `z.string().min(1).optional()`, so '' is *present-and-invalid* → HTTP 400
      // (invalid-argument) and the save silently fails. resolveLoanBookSelection
      // can yield '' for some loan-book selections; coalesce to undefined.
      templateId: resolvedParams.templateId || undefined,
      loanIds: [loanId],
      reason,
      notes,
    });
    if (ok) {
      patchReason(loanId, reason, notes);       // instant row update
      toast.success('Loss reason saved');
      refetch({ silent: true });                // reconcile cards in the background (no page reload)
    } else {
      toast.error(lastSaveError() || 'Could not save the loss reason. Please try again.');
    }
  };

  const handleSaveOutcome = async (loanId: string) => {
    const loan = dischargedLoans.find((l) => l.loanId === loanId);
    if (!loan?.refinanceMatch?.matchId || !outcomeDraft || outcomeDraft.loanId !== loanId) return;
    // Derive the WRITE status from the DISPLAY status (refinanceMatch.status), which is
    // the coerced 'confirmed' | 'pending' value — never 'rejected' for an editable row.
    // Using the RAW verificationStatus would reopen a consolidation-rejected match
    // ('rejected'→'pending') and corrupt the shared refinance_matches SSOT.
    const status: 'confirmed' | 'pending' =
      loan.refinanceMatch.status === 'confirmed' ? 'confirmed' : 'pending';
    const outcomeType = (outcomeDraft.outcomeType || undefined) as OutcomeType | undefined;
    const notes = outcomeDraft.notes || null;
    // WI8 hard-requires relationshipType on confirm; this surface has no picker
    // yet (WS5 adds one). Derive it from the row's displayed status, which the FE
    // already knows (refinanced* = refinance, returning_client* = returning client).
    const relationshipType: RelationshipType =
      loan.refinanceStatus.startsWith('returning_client') ? 'returning_client' : 'refinance';
    // Optimistic: flip the row NOW so the user never waits on the BigQuery write.
    // The verify runs in the background; a failure rolls the row back to `prev`.
    const prev = loan;
    patchOutcome(loanId, outcomeType || null, notes, status, relationshipType);
    toast.success('Outcome saved');                              // optimistic - the row already flipped
    try {
      await verifyMatch(loan.refinanceMatch.matchId, companyId, status, relationshipType, outcomeType, notes || undefined);
      refetch({ silent: true });                                 // reconcile authoritative values in the background
    } catch {
      restoreLoan(loanId, prev);                                 // roll back the optimistic flip
      toast.error('Could not save the outcome - it has been reverted. Please try again.');
    }
  };

  const handleConfirmRefi = async (loanId: string) => {
    const loan = dischargedLoans.find((l) => l.loanId === loanId);
    if (!loan?.refinanceMatch?.matchId) return;
    // verifyMatch(...,'confirmed') REQUIRES an outcomeType — calling without one throws
    // an unhandled invalid-argument. The Confirm button is disabled until an outcome is
    // picked; this is the defensive backstop.
    const outcomeType =
      outcomeDraft?.loanId === loanId && outcomeDraft.outcomeType ? outcomeDraft.outcomeType : null;
    if (!outcomeType) return;
    const notes = (outcomeDraft?.loanId === loanId ? outcomeDraft.notes : '') || null;
    // Optimistic flip; background verify; roll back on failure.
    const prev = loan;
    patchOutcome(loanId, outcomeType, notes, 'confirmed', 'refinance');
    toast.success('Refinance confirmed');                     // optimistic - the row already flipped
    try {
      // WI8 hard-requires relationshipType on confirm. The "Confirm Refinance"
      // button only renders for refinanced_pending rows, so the relationship is
      // 'refinance' (WS5 adds an explicit picker incl. returning clients).
      await verifyMatch(loan.refinanceMatch.matchId, companyId, 'confirmed', 'refinance', outcomeType as OutcomeType, notes || undefined);
      refetch({ silent: true });                              // reconcile authoritative values in the background
    } catch {
      restoreLoan(loanId, prev);
      toast.error('Could not confirm the refinance - it has been reverted. Please try again.');
    }
  };

  const handleConfirmReturning = async (loanId: string) => {
    const loan = dischargedLoans.find((l) => l.loanId === loanId);
    if (!loan?.refinanceMatch?.matchId) return;
    const notes = (outcomeDraft?.loanId === loanId ? outcomeDraft.notes : '') || null;
    // Optimistic flip to Returning; background verify; roll back on failure.
    const prev = loan;
    // Returning client carries NO outcome type (only refinances do).
    patchOutcome(loanId, null, notes, 'confirmed', 'returning_client');
    toast.success('Returning client confirmed');              // optimistic - the row already flipped
    try {
      // WI8 requires an explicit relationshipType on confirm; this is the
      // returning-client path (no outcome type).
      await verifyMatch(loan.refinanceMatch.matchId, companyId, 'confirmed', 'returning_client', undefined, notes || undefined);
      refetch({ silent: true });
    } catch {
      restoreLoan(loanId, prev);
      toast.error('Could not confirm the returning client - it has been reverted. Please try again.');
    }
  };

  const handleBulkSetReason = async (reason: LossReason, notes?: string): Promise<boolean> => {
    // Only truly-discharged rows are categorisable.
    const ids = Array.from(selectedLoanIds).filter((id) => {
      const loan = dischargedLoans.find((l) => l.loanId === id);
      return loan?.refinanceStatus === 'truly_discharged';
    });
    if (ids.length === 0) return false;
    const ok = await setLossReason({
      companyId,
      // See handleSaveReason: '' fails the callable's `min(1)` schema → 400.
      templateId: resolvedParams.templateId || undefined,
      loanIds: ids,
      reason,
      notes: notes || null,
    });
    if (ok) {
      ids.forEach((id) => patchReason(id, reason, notes || null));  // instant
      setSelectedLoanIds(new Set());
      toast.success(`Loss reason set for ${ids.length} loan${ids.length === 1 ? '' : 's'}`);
      refetch({ silent: true });                                    // reconcile (no page reload)
    } else {
      toast.error(lastSaveError() || 'Could not set the loss reason. Please try again.');
    }
    return ok;
  };

  const handleReasonCardSelect = (reason: LossReason | 'all') => {
    setReasonFilter((prev) => (prev === reason ? 'all' : reason));
    if (reason !== 'all') setViewMode('table');
  };

  const getOfficeName = (officeId: string | null): string => {
    if (!officeId) return 'Unknown';
    const office = offices?.find((o) => o.id === officeId);
    return office?.name || office?.officeName || officeId;
  };
  const hasMultipleOffices = offices && offices.length > 1;

  const formatDate = (d: Date | string): string => {
    const date = d instanceof Date ? d : new Date(d);
    return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-AU');
  };

  const statusBadge = (status: string) => {
    if (status === 'refinanced')
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-green-500/15 text-green-400 border-green-500/30">
          <Check className="w-3 h-3" /> Refinanced
        </span>
      );
    if (status === 'refinanced_pending')
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-yellow-500/15 text-yellow-400 border-yellow-500/30">
          <Clock className="w-3 h-3" /> Refi (Pending)
        </span>
      );
    if (status === 'returning_client')
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-purple-500/15 text-purple-400 border-purple-500/30">
          <Check className="w-3 h-3" /> Returning Client
        </span>
      );
    if (status === 'returning_client_pending')
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-yellow-500/15 text-yellow-400 border-yellow-500/30">
          <Clock className="w-3 h-3" /> Returning (Pending)
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-red-500/15 text-red-400 border-red-500/30">
        <XIcon className="w-3 h-3" /> Discharged
      </span>
    );
  };

  const reasonCell = (loan: any) => {
    // Pending outcomes link through to the Client Retention review queue.
    if (loan.refinanceStatus === 'refinanced_pending' || loan.refinanceStatus === 'returning_client_pending') {
      return (
        <Link
          to="/reports/refinance-intelligence"
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-cyan-500/15 text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/25"
        >
          Review
        </Link>
      );
    }
    if (loan.refinanceStatus !== 'truly_discharged') return <span className="text-gray-500 text-xs">—</span>;
    if (loan.lossReason) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border bg-gray-700/60 text-gray-200 border-gray-600">
          {LOSS_REASON_LABELS[loan.lossReason as LossReason] || loan.lossReason}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-amber-500/15 text-amber-400 border-amber-500/30">
        <AlertTriangle className="w-3 h-3" /> Needs categorisation
      </span>
    );
  };

  // ----- loading / error -----
  if (loading) {
    return <SkeletonReportPage statCards={6} statColumns={3} tableColumns={8} />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
          <h3 className="text-red-300 font-semibold mb-2">Error Loading Data</h3>
          <p className="text-red-200">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FileX2}
        title="Discharges"
        subtitle="Track discharged loans, categorise why clients left, and record your outreach"
        onRefresh={() => refetch()}
        isRefreshing={loading}
      />

      {/* Discharges | Dormant tab toggle — Dormant is legacy-book only */}
      {dormancyEnabled && (
      <div className="flex items-center gap-1 border-b border-gray-700">
        <button
          onClick={() => setActiveTab('discharges')}
          className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 flex items-center gap-2 transition-colors ${
            activeTab === 'discharges' ? 'text-blue-300 border-blue-500' : 'text-gray-400 border-transparent hover:text-gray-200'
          }`}
        >
          <FileX2 className="w-4 h-4" /> Discharges
        </button>
        <button
          onClick={() => setActiveTab('dormant')}
          className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 flex items-center gap-2 transition-colors ${
            activeTab === 'dormant' ? 'text-blue-300 border-blue-500' : 'text-gray-400 border-transparent hover:text-gray-200'
          }`}
        >
          <Moon className="w-4 h-4" /> Dormant — needs review
          {dormantNeedsReview > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[0.6875rem] font-bold bg-amber-500/15 text-amber-300">
              {dormantNeedsReview}
            </span>
          )}
        </button>
      </div>
      )}

      {dormancyEnabled && activeTab === 'dormant' && (
        <DormantLoansSection
          companyId={companyId}
          dormantLoans={dormantLoans}
          loading={dormantLoading}
          error={dormantError}
          totalLoanValueCents={dormantTotalValueCents}
          needsReviewCount={dormantNeedsReview}
          resolve={resolveDormant}
          contactsByLoan={contactsByLoan}
          mutateContact={mutateContact}
          contactBrokerList={contactBrokerList}
          currentUser={currentUser}
        />
      )}

      {(!dormancyEnabled || activeTab === 'discharges') && (<>
      {/* Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
        <h2 className="text-lg font-semibold mb-4 text-white">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {/* Year */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Year</label>
            <select
              value={yearFilter === 'all' ? 'all' : String(yearFilter)}
              onChange={(e) => setYearFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Years</option>
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Office */}
          {userData?.role !== 'broker' &&
            userData?.role !== 'broker_assistant' &&
            userVisibleOffices &&
            userVisibleOffices.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Office</label>
                <select
                  value={selectedOffice}
                  onChange={(e) => setSelectedOffice(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Offices</option>
                  {userVisibleOffices.map((o: any) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

          {/* Broker */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Broker</label>
            <select
              value={selectedBroker}
              onChange={(e) => setSelectedBroker(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Brokers</option>
              {availableBrokers.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          {/* Loan Book */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Loan Book</label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Loan Books</option>
              {getDisplayLoanBooks(loanBooks).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as typeof selectedStatus)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="truly_discharged">Truly Discharged</option>
              <option value="needs_review">Needs Review</option>
              <option value="refinanced">Refinanced</option>
              <option value="refinanced_pending">Refinanced (Pending)</option>
              <option value="returning_client">Returning Client</option>
              <option value="returning_client_pending">Returning Client (Pending)</option>
            </select>
          </div>

          {/* Categorisation */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Categorisation</label>
            <select
              value={categorizationStatus}
              onChange={(e) => setCategorizationStatus(e.target.value as typeof categorizationStatus)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="needs_categorization">Needs Categorisation</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stat cards — 6 cards, 3 per row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Total Discharged"
          value={totalDischargeCount.toLocaleString()}
          icon={<FileX2 className="w-6 h-6" />}
          gradientColor="#ef4444"
          periodLabel={periodLabel}
        />
        <StatCard
          title="Total Loan Value"
          value={formatCurrencyShorthand(totalLoanValueCents)}
          icon={<Banknote className="w-6 h-6" />}
          gradientColor="#10b981"
          periodLabel="Aggregate at discharge"
        />
        <StatCard
          title="Average Loan Size"
          value={formatCurrencyShorthand(avgLoanSizeCents)}
          icon={<Scale className="w-6 h-6" />}
          gradientColor="#3b82f6"
          periodLabel="Mean discharge amount"
        />
        <StatCard
          title="Refinance Rate"
          value={`${refinanceRate}%`}
          icon={<TrendingUp className="w-6 h-6" />}
          gradientColor="#06b6d4"
          periodLabel={`${refinancedCount.toLocaleString()} of ${(refinancedCount + trulyDischargedCount).toLocaleString()} refinanced`}
        />
        <StatCard
          title="Est. Trail Impact"
          value={`${formatCurrencyShorthand(totalLostTrailCents)}/yr`}
          icon={<TrendingDown className="w-6 h-6" />}
          gradientColor="#f59e0b"
          periodLabel="Projected trail foregone"
        />
        <StatCard
          title="Categorised"
          value={`${categorisedPct}%`}
          icon={<ListChecks className="w-6 h-6" />}
          gradientColor="#8b5cf6"
          periodLabel={`${categorisedCount.toLocaleString()} of ${trulyDischargedCount.toLocaleString()} truly-discharged`}
        />
      </div>

      {/* Client outcome row (WS3): click a card to filter the discharge table by outcome. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {([
          { key: 'truly_discharged', label: 'Truly Discharged', count: trulyDischargedCount, color: 'text-red-400', ring: 'border-red-500 ring-red-500/40' },
          { key: 'needs_review', label: 'Needs Review', count: refinancedPendingCount + returningClientPendingCount, color: 'text-yellow-400', ring: 'border-yellow-500 ring-yellow-500/40' },
          { key: 'refinanced', label: 'Refinanced', count: refinancedCount, color: 'text-green-400', ring: 'border-green-500 ring-green-500/40' },
          { key: 'returning_client', label: 'Returning Clients', count: returningClientCount, color: 'text-purple-400', ring: 'border-purple-500 ring-purple-500/40' },
        ] as { key: typeof selectedStatus; label: string; count: number; color: string; ring: string }[]).map((o) => {
          const active = selectedStatus === o.key;
          return (
            <button
              key={o.key}
              onClick={() => { setSelectedStatus(active ? 'all' : o.key); setViewMode('table'); }}
              className={`bg-gray-800 rounded-lg p-4 border text-left transition-all hover:bg-gray-750 ${active ? `${o.ring} ring-1` : 'border-gray-700'}`}
            >
              <p className="text-xs text-gray-400 mb-1">{o.label}</p>
              <p className={`text-2xl font-bold ${o.color}`}>{o.count.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">{active ? 'Click to clear' : 'Click to filter'}</p>
            </button>
          );
        })}
      </div>

      {/* Charts / Table toggle (Dashboard pill) */}
      <div className="flex gap-1 bg-gray-700 rounded-lg p-1 w-fit">
        <button
          onClick={() => setViewMode('charts')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'charts' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:text-white'
          }`}
        >
          <BarChart3 className="w-4 h-4" /> Charts
        </button>
        <button
          onClick={() => setViewMode('table')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:text-white'
          }`}
        >
          <TableIcon className="w-4 h-4" /> Table
        </button>
      </div>

      {/* ===================== CHARTS VIEW ===================== */}
      {viewMode === 'charts' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Trend */}
            <div className="lg:col-span-2 bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-400" /> {trendTitle}
                </h2>
                <span className="text-sm text-gray-400">Showing {totalDischargeCount} discharges</span>
              </div>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="label" stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} />
                    <YAxis
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF' }}
                      allowDecimals={false}
                      label={{ value: 'Count', angle: -90, position: 'insideLeft', fill: '#9CA3AF' }}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                    <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                    <Bar dataKey="count" fill="#3B82F6" name="Discharge Count" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <p>No discharge data available</p>
                </div>
              )}
            </div>

            {/* Refinance split donut */}
            <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-6">
                <GitCompareArrows className="w-5 h-5 text-blue-400" /> Refinance Split
              </h2>
              <div className="flex flex-col items-center gap-5">
                <div
                  className="relative w-[10.625rem] h-[10.625rem] rounded-full flex items-center justify-center"
                  style={{
                    background: `conic-gradient(#10b981 0deg ${refinanceRate * 3.6}deg, #ef4444 ${refinanceRate * 3.6}deg 360deg)`,
                  }}
                >
                  <div className="absolute w-[6.75rem] h-[6.75rem] rounded-full bg-gray-800" />
                  <div className="relative z-10 text-center">
                    <div className="text-3xl font-bold text-white">{refinanceRate}%</div>
                    <div className="text-[0.66rem] uppercase tracking-wide font-bold text-gray-500">Refinanced</div>
                  </div>
                </div>
                <div className="w-full">
                  {refinanceSplit.map((seg) => (
                    <div
                      key={seg.name}
                      className="flex items-center justify-between text-sm py-1.5 border-b border-gray-700/60 last:border-0"
                    >
                      <span className="flex items-center gap-2 text-gray-300">
                        <span className="w-3 h-3 rounded" style={{ background: seg.color }} />
                        {seg.name}
                      </span>
                      <span className="font-bold text-white">
                        {seg.value}
                        <small className="text-gray-500 font-medium ml-1.5">
                          {formatCurrencyShorthand(seg.valueCents)}
                        </small>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Loss reason categorisation breakdown */}
          <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <SearchCheck className="w-5 h-5 text-blue-400" /> Why Clients Left — Categorisation
              </h2>
              <span className="text-sm text-gray-500">
                Truly-discharged loans only · click a card to filter the table
              </span>
            </div>
            <LossReasonStatCards
              metrics={lossMetrics}
              selectedReason={reasonFilter}
              onReasonSelect={handleReasonCardSelect}
              isLoading={loading}
            />
          </div>

          {/* Broker breakdown (collapsible) */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
            <button
              onClick={() => setBrokerSectionCollapsed(!brokerSectionCollapsed)}
              className="w-full p-6 border-b border-gray-700 flex justify-between items-center hover:bg-gray-700/40 transition-colors"
            >
              <div className="text-left">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-400" /> Breakdown by Broker
                </h2>
                <p className="text-sm text-gray-400 mt-1">Discharge &amp; retention performance by team member</p>
              </div>
              <ChevronDown
                className={`w-6 h-6 text-gray-400 transition-transform ${brokerSectionCollapsed ? '' : 'rotate-180'}`}
              />
            </button>
            {!brokerSectionCollapsed &&
              (sortedBrokers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-900 border-b border-gray-700">
                      <tr>
                        {([
                          ['broker', 'Broker'],
                          ['count', 'Discharged'],
                          ['totalValue', 'Total Value'],
                          ['refinancedCount', 'Refinanced'],
                          ['returningCount', 'Returning'],
                          ['retention', 'Retention'],
                          ['trail', 'Est. Trail Impact'],
                        ] as [typeof brokerSortColumn, string][]).map(([col, label]) => (
                          <th
                            key={col}
                            onClick={() => handleBrokerSort(col)}
                            className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800"
                          >
                            {label}
                            <span className="ml-1 text-gray-600">
                              {brokerSortColumn === col ? (brokerSortDirection === 'asc' ? '↑' : '↓') : '↕'}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {sortedBrokers.map((broker, idx) => (
                        <tr key={broker.brokerId || idx} className="hover:bg-gray-700 transition-colors">
                          <td className="px-3 py-3 text-sm font-medium text-white">{broker.broker}</td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-300">{broker.count}</td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm font-semibold text-red-400">
                            {formatCurrency(broker.totalLoanValueCents)}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-green-400">{broker.refinancedCount}</td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-purple-400">{broker.returningCount}</td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border ${
                                broker.retention >= 40
                                  ? 'bg-green-500/15 text-green-400 border-green-500/30'
                                  : broker.retention >= 20
                                  ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30'
                                  : 'bg-red-500/15 text-red-400 border-red-500/30'
                              }`}
                            >
                              {broker.retention}%
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-amber-400">
                            {formatCurrencyShorthand(broker.trail)}/yr
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">No broker data available for the selected filters</div>
              ))}
          </div>
        </div>
      )}

      {/* ============ SEARCH - always visible, filters the discharge list below
              (client-side; same inline pattern as CustomerPortfolioPage). ============ */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search discharges by customer, broker, or lender..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              aria-label="Clear search"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* ============ DISCHARGE LIST — always visible (Charts shows it below the
              charts; Table shows just this). Filtering by broker/year always yields a
              list for the current filters, in either view. ============ */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <TableIcon className="w-5 h-5 text-blue-400" /> All Discharges
            </h2>
            <div className="flex items-center gap-3">
              {reengagementEnabled &&
                (userData?.role === 'super_admin' || userData?.role === 'administrator') && (
                  <Link
                    to="/discharged-reengagement-settings"
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
                    title="Configure the discharged re-engagement campaign and follow-up email"
                  >
                    <SettingsIcon className="h-4 w-4" /> Re-engage settings
                  </Link>
                )}
              <span className="text-sm text-gray-500">{sortedLoans.length} discharges</span>
            </div>
          </div>

          {sortedLoans.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900 border-b border-gray-700">
                  <tr>
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={toggleSelectAllOnPage}
                        className="w-4 h-4 accent-blue-600 cursor-pointer"
                      />
                    </th>
                    <th
                      onClick={() => handleDetailSort('customer')}
                      className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800"
                    >
                      Customer <SortIndicator column="customer" />
                    </th>
                    <th
                      onClick={() => handleDetailSort('broker')}
                      className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800"
                    >
                      Broker <SortIndicator column="broker" />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Lender
                    </th>
                    <th
                      onClick={() => handleDetailSort('dischargeDate')}
                      className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800"
                    >
                      Discharge Date <SortIndicator column="dischargeDate" />
                    </th>
                    <th
                      onClick={() => handleDetailSort('loanAmount')}
                      className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800"
                    >
                      Loan Amount <SortIndicator column="loanAmount" />
                    </th>
                    <th
                      onClick={() => handleDetailSort('status')}
                      className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800"
                    >
                      Status <SortIndicator column="status" />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Reason
                    </th>
                    {reengagementEnabled && (
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Re-engage
                      </th>
                    )}
                    <th className="px-3 py-3 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {paginatedLoans.map((loan, idx) => {
                    const isExpanded = expandedRow === loan.loanId;
                    const isTruly = loan.refinanceStatus === 'truly_discharged';
                    return (
                      <React.Fragment key={loan.loanId || idx}>
                        <tr
                          onClick={() => toggleExpand(loan.loanId)}
                          className={`transition-colors cursor-pointer ${
                            isExpanded ? 'bg-gray-700/40' : 'hover:bg-gray-700/40'
                          }`}
                          title="Click to open"
                        >
                          <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedLoanIds.has(loan.loanId)}
                              onChange={() => toggleSelect(loan.loanId)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 accent-blue-600 cursor-pointer"
                            />
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                                {(loan.customerName || '?')
                                  .replace(/&|the|family/gi, '')
                                  .trim()
                                  .split(/\s+/)
                                  .slice(0, 2)
                                  .map((w: string) => w[0])
                                  .join('')
                                  .toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold text-white truncate">{loan.customerName}</div>
                                <div className="text-[0.68rem] text-gray-500 font-mono">{loan.loanId}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-300">{loan.broker}</td>
                          <td className="px-3 py-3 text-sm text-gray-300">{loan.lender || '—'}</td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-300">
                            {formatDate(loan.dischargeDate)}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm font-bold text-white">
                            {formatCurrency(loan.loanAmountCents)}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">{statusBadge(loan.refinanceStatus)}</td>
                          <td className="px-3 py-3 whitespace-nowrap">{reasonCell(loan)}</td>
                          {reengagementEnabled && (
                            <td className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                              <ReengagementActionCell
                                companyId={companyId}
                                loan={{ loanId: loan.loanId, customerName: loan.customerName }}
                                loanBroker={loan.broker}
                                eligibility={reengagementByLoan[loan.loanId]}
                                config={null}
                                loading={reengagementLoading}
                                contactEntries={contactsByLoan[loan.loanId] || []}
                                onMutateContact={mutateContact}
                                availableBrokers={contactBrokerList}
                                currentUser={currentUser}
                                onChanged={refetchReengagement}
                              />
                            </td>
                          )}
                          <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => toggleExpand(loan.loanId)}
                              className="text-gray-500 hover:text-white"
                            >
                              <ChevronRight
                                className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              />
                            </button>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="bg-gray-900/55">
                            <td colSpan={reengagementEnabled ? 10 : 9} className="px-4 py-5 sm:pl-12">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                {/* LEFT: reason editor (truly-discharged) OR refi + outcome editor */}
                                {isTruly ? (
                                  <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                                    <h4 className="text-xs uppercase tracking-wide text-gray-500 font-bold mb-3 flex items-center gap-1.5">
                                      <SearchCheck className="w-3.5 h-3.5 text-blue-400" /> Why did this client leave?
                                    </h4>
                                    <div className="mb-3">
                                      <label className="block text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold mb-1.5">
                                        Loss Reason
                                      </label>
                                      <select
                                        value={reasonDraft?.loanId === loan.loanId ? reasonDraft.reason : (loan.lossReason || 'unknown')}
                                        onChange={(e) =>
                                          setReasonDraft({
                                            loanId: loan.loanId,
                                            reason: e.target.value as LossReason,
                                            notes: reasonDraft?.loanId === loan.loanId ? reasonDraft.notes : (loan.lossNotes || ''),
                                          })
                                        }
                                        disabled={isBrokerFilteredAdmin}
                                        title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
                                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                      >
                                        {STANDARD_LOSS_REASONS.map((r) => (
                                          <option key={r} value={r}>
                                            {LOSS_REASON_LABELS[r]}
                                          </option>
                                        ))}
                                        <UncontactableLossOption
                                          eligibility={reengagementByLoan[loan.loanId]}
                                          currentReason={(loan.lossReason || 'unknown') as LossReason}
                                          reengagementEnabled={reengagementEnabled}
                                        />
                                        {/* A retained disposition is written only from the Discharge
                                            Watch-List (setLossReason rejects it). Render an existing
                                            value so the select tells the truth, but never offer it. */}
                                        {isRetainedLossReason(loan.lossReason) && (
                                          <option value={loan.lossReason as LossReason} disabled>
                                            {LOSS_REASON_LABELS[loan.lossReason as LossReason]} (retained, set on the watch list)
                                          </option>
                                        )}
                                      </select>
                                    </div>
                                    <div className="mb-3">
                                      <label className="block text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold mb-1.5">
                                        Notes (optional)
                                      </label>
                                      <textarea
                                        value={reasonDraft?.loanId === loan.loanId ? reasonDraft.notes : (loan.lossNotes || '')}
                                        onChange={(e) =>
                                          setReasonDraft({
                                            loanId: loan.loanId,
                                            reason: (reasonDraft?.loanId === loan.loanId ? reasonDraft.reason : (loan.lossReason || 'unknown')) as LossReason,
                                            notes: e.target.value,
                                          })
                                        }
                                        rows={2}
                                        maxLength={LOSS_NOTES_MAX_LENGTH}
                                        disabled={isBrokerFilteredAdmin}
                                        title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
                                        placeholder="Add context about why this loan was lost…"
                                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                                      />
                                      {(() => {
                                        const len = (reasonDraft?.loanId === loan.loanId ? reasonDraft.notes : (loan.lossNotes || '')).length;
                                        return (
                                          <div className={`mt-1 text-right text-[11px] ${len >= LOSS_NOTES_MAX_LENGTH ? 'text-red-400' : len > LOSS_NOTES_MAX_LENGTH * 0.9 ? 'text-amber-400' : 'text-gray-500'}`}>
                                            {len.toLocaleString()}/{LOSS_NOTES_MAX_LENGTH.toLocaleString()}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    <button
                                      onClick={() => handleSaveReason(loan.loanId)}
                                      disabled={isBrokerFilteredAdmin || isSavingReason}
                                      title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
                                      className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                      <Save className="w-3.5 h-3.5" />
                                      {isSavingReason ? 'Saving…' : 'Save Reason'}
                                    </button>
                                    {loan.lossReason && loan.lossReasonSetByEmail && (
                                      <div className="text-xs text-gray-500 mt-2">
                                        Set by {loan.lossReasonSetByEmail}
                                        {loan.lossReasonSetAt ? ` on ${formatDate(loan.lossReasonSetAt)}` : ''}
                                      </div>
                                    )}
                                    {canClassify && (
                                      <div className="mt-4 pt-3 border-t border-gray-700/60">
                                        <p className="text-xs text-gray-500 mb-2">
                                          Client actually returned or refinanced? Link the successor loan to record it as retained.
                                        </p>
                                        <ManualLinkControl
                                          companyId={companyId}
                                          canManualLink={canClassify}
                                          presetDischargedLoanId={loan.loanId}
                                          onCreate={createManualRefinanceMatch}
                                          onCreated={() => refetch({ silent: true })}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ) : loan.refinanceMatch ? (
                                  <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                                    <h4 className="text-xs uppercase tracking-wide text-gray-500 font-bold mb-3 flex items-center justify-between gap-2">
                                      <span className="flex items-center gap-1.5">
                                        <RefreshCw className="w-3.5 h-3.5 text-blue-400" /> Client Outcome Details
                                      </span>
                                      {loan.refinanceStatus === 'refinanced_pending' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-yellow-500/15 text-yellow-400 border-yellow-500/30 normal-case">
                                          <Clock className="w-3 h-3" /> Pending verification
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-green-500/15 text-green-400 border-green-500/30 normal-case">
                                          <Check className="w-3 h-3" /> Confirmed
                                        </span>
                                      )}
                                    </h4>

                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm">
                                      <div>
                                        <div className="text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold">New Loan ID</div>
                                        <div className="text-white font-mono">{loan.refinanceMatch.newLoanId}</div>
                                      </div>
                                      <div>
                                        <div className="text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold">Lender</div>
                                        <div className="text-white">{loan.refinanceMatch.newLender || 'Unknown'}</div>
                                      </div>
                                      <div>
                                        <div className="text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold">Settlement</div>
                                        <div className="text-white">{formatDate(loan.refinanceMatch.newSettlementDate)}</div>
                                      </div>
                                      <div>
                                        <div className="text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold">New Amount</div>
                                        <div className="text-green-400 font-semibold">
                                          {formatCurrency(loan.refinanceMatch.newLoanAmountCents)}
                                        </div>
                                      </div>
                                      <div>
                                        <div className="text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold">Broker</div>
                                        <div className="text-white">{loan.refinanceMatch.newBrokerDisplay || 'Unknown'}</div>
                                      </div>
                                      {hasMultipleOffices && loan.refinanceMatch.newOfficeId && (
                                        <div>
                                          <div className="text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold">Office</div>
                                          <div className="text-white">{getOfficeName(loan.refinanceMatch.newOfficeId)}</div>
                                        </div>
                                      )}
                                    </div>

                                    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-700/60 text-sm text-gray-400 flex-wrap">
                                      <span>
                                        {loan.lender || '—'} {formatCurrency(loan.loanAmountCents)}
                                      </span>
                                      <ArrowRight className="w-3.5 h-3.5 text-gray-500" />
                                      <span className="text-green-400 font-semibold">
                                        {loan.refinanceMatch.newLender || '—'} {formatCurrency(loan.refinanceMatch.newLoanAmountCents)}
                                      </span>
                                    </div>

                                    {/* Outcome editor — writes the shared refinance store (also editable on
                                        Refinance Intelligence). No internal table/code names in the UI. */}
                                    <div className="mt-4 pt-3 border-t border-gray-700/60">
                                      <label className="block text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold mb-1.5">
                                        Outcome Type
                                      </label>
                                      <select
                                        value={outcomeDraft?.loanId === loan.loanId ? outcomeDraft.outcomeType : (loan.refinanceMatch.outcomeType || '')}
                                        onChange={(e) =>
                                          setOutcomeDraft({
                                            loanId: loan.loanId,
                                            outcomeType: e.target.value as OutcomeType | '',
                                            notes: outcomeDraft?.loanId === loan.loanId ? outcomeDraft.notes : (loan.refinanceMatch.verificationNotes || ''),
                                          })
                                        }
                                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      >
                                        <option value="">Select outcome…</option>
                                        {OUTCOME_KEYS.map((k) => (
                                          <option key={k} value={k}>
                                            {OUTCOME_LABELS[k]}
                                          </option>
                                        ))}
                                      </select>
                                      <div className="mt-2.5">
                                        <label className="block text-[0.62rem] uppercase tracking-wide text-gray-500 font-bold mb-1.5">
                                          Verification notes (optional)
                                        </label>
                                        <input
                                          type="text"
                                          value={outcomeDraft?.loanId === loan.loanId ? outcomeDraft.notes : (loan.refinanceMatch.verificationNotes || '')}
                                          onChange={(e) =>
                                            setOutcomeDraft({
                                              loanId: loan.loanId,
                                              outcomeType: (outcomeDraft?.loanId === loan.loanId ? outcomeDraft.outcomeType : (loan.refinanceMatch.outcomeType || '')) as OutcomeType | '',
                                              notes: e.target.value,
                                            })
                                          }
                                          placeholder="Notes…"
                                          className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                      </div>
                                      <div className="flex gap-2 flex-wrap mt-3">
                                        <button
                                          onClick={() => handleSaveOutcome(loan.loanId)}
                                          disabled={isVerifying}
                                          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                          <Save className="w-3.5 h-3.5" />
                                          {isVerifying ? 'Saving…' : 'Save Outcome'}
                                        </button>
                                        {loan.refinanceStatus === 'refinanced_pending' && (
                                          <button
                                            onClick={() => handleConfirmRefi(loan.loanId)}
                                            disabled={
                                              isVerifying ||
                                              !(outcomeDraft?.loanId === loan.loanId && outcomeDraft.outcomeType)
                                            }
                                            title={
                                              outcomeDraft?.loanId === loan.loanId && outcomeDraft.outcomeType
                                                ? undefined
                                                : 'Select an outcome type before confirming'
                                            }
                                            className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                          >
                                            <Check className="w-3.5 h-3.5" /> Confirm Refinance
                                          </button>
                                        )}
                                        {canClassify && loan.refinanceStatus === 'returning_client_pending' && (
                                          <button
                                            onClick={() => handleConfirmReturning(loan.loanId)}
                                            disabled={isVerifying}
                                            className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                          >
                                            <Check className="w-3.5 h-3.5" /> Confirm Returning Client
                                          </button>
                                        )}
                                      </div>
                                      {loan.refinanceMatch.verifiedByEmail && (
                                        <div className="text-xs text-gray-500 mt-2">
                                          Set by {loan.refinanceMatch.verifiedByEmail}
                                          {loan.refinanceMatch.verifiedAt ? ` on ${formatDate(loan.refinanceMatch.verifiedAt)}` : ''}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 text-sm text-gray-400">
                                    No refinance match details available for this loan.
                                  </div>
                                )}

                                {/* RIGHT: unified contact timeline (canonical store) */}
                                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                                  {companyId && (() => {
                                    const periodMonth = periodMonthFromDate(loan.dischargeDate);
                                    if (!periodMonth) return null;
                                    const status = trailRiskByRef.get(
                                      refKey({ loanId: loan.loanId, eventType: 'loan_discharged', periodMonth }),
                                    );
                                    // Nothing to say and no failure to report: stay silent.
                                    if (!status && !trailRiskError) return null;
                                    return (
                                      <div className="mb-3">
                                        <TrailRiskFollowUp
                                          companyId={companyId}
                                          status={status}
                                          statusUnavailable={Boolean(trailRiskError)}
                                          onChanged={refetchTrailRisk}
                                        />
                                      </div>
                                    );
                                  })()}
                                  <ContactTimeline
                                    companyId={companyId}
                                    loanId={loan.loanId}
                                    customerName={loan.customerName}
                                    loanBroker={loan.broker}
                                    sourcePage="Discharges"
                                    entries={contactsByLoan[loan.loanId] || []}
                                    availableBrokers={contactBrokerList}
                                    currentUser={currentUser}
                                    onMutate={mutateContact}
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">No discharge records available for the selected filters</div>
          )}

          {sortedLoans.length > 0 && (
            <div className="px-6 py-3 bg-gray-900 border-t border-gray-700 flex justify-between items-center">
              <span className="text-sm text-gray-400">
                Showing {sortedLoans.length ? startIdx + 1 : 0} – {endIdx} of {sortedLoans.length} records
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white rounded transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-300">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white rounded transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      {/* Bulk action bar — bulk set loss reason on selected truly-discharged rows */}
      <BulkActionBar
        selectedCount={selectedLoanIds.size}
        onSetReason={handleBulkSetReason}
        onClearSelection={() => setSelectedLoanIds(new Set())}
        isSaving={isSavingReason}
      />
      </>)}
    </div>
  );
};

export default DischargedLoansPage;
